import { useParams, Link } from 'react-router-dom';
import './styles.css';

const blogPosts = {
    1: {
      title: 'Simulating an ADC in Python',
      date: 'December 30, 2024',
      // imageUrl: "/images/blog/adc_simulation.gif",
      content: (
        <>
          <div className="container mt-5">
            <div className="row justify-content-center">
            <img src="/images/blog/adc_simulation.gif" alt="ADC simulation" className="responsive-image-l"></img>
              <div className="col-md-8">
                <h1 className="text-center">Simulating an ADC in Python </h1>
                <h4 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h4>
                <p className="text-muted text-center">December 30, 2024</p>
                <p>This Python code simulates the behavior of an ADC, helping to visualize how the conversion process works and understand the impact of various factors.</p>
                
                <p>You can modify the following parameters:</p>
                <ul>
                    <li><strong>System Electrical Parameters:</strong>
                    <ul>
                        <li>adc_resolution_bits = 4 (ADC resolution bits)</li>
                        <li>reference_voltage = 5.0 V (Reference voltage)</li>
                        <li>input_signal_amplitude = 2.5 V (Signal amplitude)</li>
                    </ul>
                    </li>
                    <li><strong>Simulation Parameters:</strong>
                    <ul>
                        <li>simulation_start_time = 0 (Simulation start time)</li>
                        <li>simulation_window_size = 1000 (Maximum number of simulation points)</li>
                        <li>frequency = 100 Hz (Signal frequency)</li>
                    </ul>
                    </li>
                </ul>
                <p><strong>What the Code Does:</strong></p>
                <ol>
                    <li>Imports necessary libraries (numpy and matplotlib).</li>
                    <li>Defines a function <code>simulate_adc</code> that takes:
                        <ul>
                            <li><code>analog_signal</code>: The analog input signal as a NumPy array.</li>
                            <li><code>bits</code>: The number of bits in the ADC (resolution).</li>
                            <li><code>v_ref</code>: The reference voltage of the ADC.</li>
                        </ul>
                    </li>
                    <li>Calculates the quantization levels and step size based on the reference voltage and resolution.</li>
                    <li>Rounds the analog signal values to the nearest quantization level.</li>
                    <li>Clamps the digital values to the valid range (0 to <code>quantization_levels - 1</code>).</li>
                    <li>Converts the resulting digital values to integers.</li>
                    <li>Sets up system parameters like resolution, reference voltage, and input signal amplitude.</li>
                    <li>Defines simulation parameters like start time, window size, and frequency.</li>
                    <li>Calculates the converted frequency based on window size.</li>
                    <li>Calculates the simulation end time based on the input frequency.</li>
                    <li>Generates a time array for the simulation.</li>
                    <li>Creates a sine wave as the analog input signal.</li>
                    <li>Simulates the ADC conversion using the <code>simulate_adc</code> function.</li>
                    <li>Plots both the analog and reconstructed digital signals using matplotlib.</li>
                </ol>
                <pre className="bg-light p-3 rounded">
                  <code>
                    {`
import numpy as np
import matplotlib.pyplot as plt
from matplotlib.animation import FuncAnimation, PillowWriter


def simulate_adc(analog_signal, bits, v_ref):
    """
    Simulates an ADC conversion.
    Args:
        analog_signal: The analog input signal (a NumPy array).
        bits: The number of bits of the ADC.
        v_ref: The reference voltage of the ADC.

    Returns: A NumPy array of digital values.
    """
    quantization_levels = 2 ** bits
    quantization_step = v_ref / quantization_levels
    digital_values = np.round(analog_signal / quantization_step)
    # Clamp values to the valid range
    digital_values = np.clip(digital_values, 0, quantization_levels - 1)
    return digital_values.astype(int)


# System Electrical Parameters:
reference_voltage = 5.0  # V reference voltage
input_signal_amplitude = 2.5  # V Signal amplitude

# Simulation Parameters:
simulation_start_time = 0  # Simulation start time
simulation_window_size = 1000  # Maximum number of simulation points
frequency = 1  # Signal frequency in Hz

# Simulate ADC
simulation_end_time = 1 / frequency
time = np.linspace(simulation_start_time, simulation_end_time, simulation_window_size)
analog_signal = input_signal_amplitude * np.sin(2 * np.pi * frequency * time)  # 1 Hz sine wave

# Prepare the figure
fig, ax = plt.subplots(figsize=(10, 6))
ax.set_title("ADC Simulation with Varying Resolutions")
ax.set_xlabel("Time (s)")
ax.set_ylabel("Voltage (V)")
ax.grid(True)

analog_line, = ax.plot(time, analog_signal, label="Analog Signal", color="blue")
digital_line, = ax.plot([], [], label="Digital Signal (Reconstructed)", color="orange")
ax.legend()


# Animation function
def update(bits):
    digital_signal = simulate_adc(analog_signal, bits, reference_voltage)
    reconstructed_signal = digital_signal * (reference_voltage / (2 ** bits))
    digital_line.set_data(time, reconstructed_signal)
    ax.set_title(f"ADC Simulation: {bits}-bit Resolution")
    return digital_line,


# Configure animation
bit_resolutions = range(2, 17)  # From 2-bit to 16-bit ADC
ani = FuncAnimation(fig, update, frames=bit_resolutions, blit=True)

# Save animation as GIF in the background
ani.save("adc_simulation.gif", writer=PillowWriter(fps=2))

# Display in real-time
plt.show()

                    `}
                  </code>
                </pre>
                
                <div className="text-center">
                <h4>Visual Results</h4>
                    <img
                        src="../images/2-5V 1kHz 4bit resolution.jpg"
                        alt="4-bit ADC"
                        className="img-fluid rounded mb-3"
                        style={{ maxWidth: '75%' }}
                    />
                    <img
                        src="../images/2-5V 1kHz 16bit resolution.jpg"
                        alt="16-bit ADC"
                        className="img-fluid rounded mb-3"
                        style={{ maxWidth: '75%' }}
                    />
                </div>
                <p>
                  This example provides a great starting point for learning about ADC simulations!
                </p>
              </div>
            </div>
          </div>
        </>
      ),
      //imageUrl: '/images/ADC-resolution.jpg',
    },
    2: {
        title: 'Basic Elements of a Digital Communication System',
        date: 'December 30, 2024',
        imageUrl: "/images/digital_communication_system.svg",
        content: (
            <>
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <h2 className="text-center">Basic Elements of a Digital Communication System</h2><br></br>
                        <h5 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h5>

                        
                        <p className="text-muted text-center">December 30, 2024</p>
                        <p>In our increasingly connected world, digital communication systems play a critical role in enabling efficient and reliable exchange of information. 
                            Every digital communication system consists of several fundamental components that work together to transmit data from a source to a destination. 
                            Let's break down the key elements of this process.</p>

                        <ol>
                            <li>
                                <h5>Information Source and Input Transducer</h5>
                                <p>The process begins with the information source, which generates the message to be transmitted. This message could be text, audio, video, or any other 
                                    type of data. An input transducer converts this message into a suitable electrical signal for further processing. For example, a microphone converts 
                                    sound waves into electrical signals in audio communication.</p>
                                    <img src="../images/transducer.svg" alt="transducer" className="responsive-image-l"/>
                                    <p>Convert electret capsule microphone into an output voltage. <a href="https://www.ti.com/tool/TIPD181" target="blank">Transducer: TI Reference Design</a></p>
                                    
                                    <h5>Reasons for Signal Conditioning</h5>
                                    <ul>
                                    <li><b>Amplification</b>: Microphones typically produce very low-level signals, which may not be sufficient for the ADC to process. A signal conditioning circuit can amplify the signal to a level that's suitable for the ADC.</li>
                                    <li><b>Impedance Matching</b>: Microphones often have high impedance outputs, while ADCs typically require low impedance inputs. A signal conditioning circuit can provide impedance matching to ensure maximum signal transfer and minimize signal loss.</li>
                                    <li><b>Noise Filtering</b>: Microphones can pick up unwanted noise, such as electromagnetic interference (EMI) or radio-frequency interference (RFI). A signal conditioning circuit can include filters to remove or attenuate these unwanted signals.</li>
                                    <li><b>Biasing and Decoupling</b>: Some microphones require biasing or decoupling to operate correctly. A signal conditioning circuit can provide these functions.</li>
                                    </ul>
                                    <h5>Types of Signal Conditioning Circuits</h5>
                                    <ul>
                                    <li><b>Operational Amplifier (Op-Amp) Circuits</b>: Op-amps are commonly used for signal amplification, impedance matching, and filtering.</li>
                                    <li><b>Instrumentation Amplifier (In-Amp) Circuits</b>: In-amps are specialized op-amps designed for precision amplification and filtering of low-level signals.</li>
                                    <li><b>Active Filters</b>: Active filters use op-amps or other active devices to filter out unwanted signals.</li>
                                    </ul>
                                    <h5>When to Use Signal Conditioning</h5>
                                    <ul>
                                    <li><b>Low-Level Signals</b>: If the microphone signal is very low (e.g., millivolts), signal conditioning is likely necessary.</li>
                                    <li><b>Noise-Sensitive Applications</b>: If the application requires high signal-to-noise ratio (SNR), signal conditioning can help remove unwanted noise.</li>
                                    <li><b>Impedance Mismatch</b>: If there's an impedance mismatch between the microphone and ADC, signal conditioning can provide impedance matching.</li>
                                    </ul>
                            </li>
                            <li>
                                <h5>Source Encoder</h5>
                                <p>A "source encoder" is a device or process in digital communication that converts raw information (like audio, video, or text) into a compressed binary 
                                    sequence of bits, essentially removing redundancy from the data to make it more efficient for transmission or storage, by assigning codewords to the 
                                    input symbols based on their probability of occurrence; it's the first stage in a 
                                    data transmission system, before the channel encoder.</p>
                                    <img src="../images/blog/source_encouder.svg" alt="transducer" className="responsive-image-l"/>
                                    <a href="https://www.ti.com/tool/AUDIO-AM62D-EVM" target="blank">TI Reference Design</a>
                                    <ul>
                                        <li><strong>Source:</strong> The original information.</li>
                                        <li><strong>Sampler:</strong> Converts analog to discrete-time.</li>
                                        <li><strong>Quantizer:</strong> Converts continuous values to discrete values.</li>
                                        <li><strong>Compression Algorithm:</strong> Reduces redundancy.</li>
                                    </ul>
                                    <br></br>
                                    This Python code defines a SourceEncoder class that simulates the process of generating symbols from a source based on given probabilities.
                                    <pre className="bg-light p-3 rounded">
                                        <code>
{`import numpy as np
import random


class SourceEncoder:
    def __init__(self, alphabet, probabilities):
        """
        Initializes the Source Encoder.
        Args:
            alphabet: A list of symbols in the source alphabet.
            probabilities: A list of probabilities corresponding to each symbol.
                           Must sum to 1.
        Raises:
            ValueError: If the probabilities do not sum to 1 or if the alphabet
                        and probabilities have different lengths.
        """

        if len(alphabet) != len(probabilities):
            raise ValueError("Alphabet and probabilities must have the same length.")
        if not np.isclose(sum(probabilities), 1.0):
            raise ValueError("Probabilities must sum to 1.")

        self.alphabet = alphabet
        self.probabilities = probabilities
        self.cumulative_probabilities = np.cumsum(probabilities)

    def generate_symbol(self):
        """
        Generates a symbol based on the given probabilities.
        Returns:
            A randomly generated symbol from the alphabet.
        """

        rand_num = random.random()
        for i, cum_prob in enumerate(self.cumulative_probabilities):
            if rand_num < cum_prob:
                return self.alphabet[i]
        return self.alphabet[-1]  # Should not happen due to cumulative probabilities

    def generate_sequence(self, length):
        """
        Generates a sequence of symbols of a given length.
        Args:
            length: The desired length of the sequence.

        Returns:
            A string representing the generated sequence.
        """

        sequence = ""
        for _ in range(length):
            sequence += self.generate_symbol()
        return sequence


# Input data
alphabet = ['A', 'B', 'C', 'D']
probabilities = [0.4, 0.3, 0.2, 0.1]

encoder = SourceEncoder(alphabet, probabilities)

# Generate a single symbol
symbol = encoder.generate_symbol()
print(f"Generated symbol: {symbol}")

# Generate a sequence of 10 symbols
sequence = encoder.generate_sequence(10)
print(f"Generated sequence: {sequence}")

# Example with binary source:
binary_alphabet = ['0', '1']
binary_probabilities = [0.7, 0.3]

binary_encoder = SourceEncoder(binary_alphabet, binary_probabilities)
binary_sequence = binary_encoder.generate_sequence(20)
print(f"Generated binary sequence: {binary_sequence}")

# Demonstrating error handling:
try:
    bad_probabilities = [0.2, 0.3, 0.4]  # sums to 0.9, not 1
    bad_encoder = SourceEncoder(alphabet, bad_probabilities)
except ValueError as e:
    print(f"Caught expected error (probabilities sum): {e}")

try:
    mismatched_probabilities = [0.2, 0.3]  # different length than alphabet
    bad_encoder2 = SourceEncoder(alphabet, mismatched_probabilities)
except ValueError as e:
    print(f"Caught expected error (mismatched lengths): {e}")

try:
    alphabet2 = ['A', 'B', 'C']
    probabilities2 = [0.2, 0.3, 0.4, 0.1]
    bad_encoder3 = SourceEncoder(alphabet2, probabilities2)
except ValueError as e:
    print(f"Caught expected error (mismatched lengths): {e}")`}

                                        </code>
                                    </pre>
                                    <p>
                                        Run 1: <br></br>
                                        Generated symbol: A <br></br>
                                        Generated sequence: CBAACBCABA<br></br>
                                        Generated binary sequence: 01011001100100000100<br></br>
                                        Caught expected error (probabilities sum): Alphabet and probabilities must have the same length.<br></br>
                                        Caught expected error (mismatched lengths): Alphabet and probabilities must have the same length.<br></br>
                                        Caught expected error (mismatched lengths): Alphabet and probabilities must have the same length.<br></br>
                                        Run 2:<br></br>
                                        Generated symbol: C<br></br>
                                        Generated sequence: BABAAAAACB<br></br>
                                        Generated binary sequence: 00010001100011110110<br></br>
                                        Caught expected error (probabilities sum): Alphabet and probabilities must have the same length.<br></br>
                                        Caught expected error (mismatched lengths): Alphabet and probabilities must have the same length.<br></br>
                                        Caught expected error (mismatched lengths): Alphabet and probabilities must have the same length.<br></br>
                                    </p>
                            </li>
                            <li>
                                <h5>Channel Encoder</h5>
                                <p>To protect the data from potential errors introduced during transmission, the channel encoder adds redundancy in a structured way. Techniques such as 
                                    error-detecting and error-correcting codes are applied to ensure that the receiver can identify and correct errors that might occur due to noise or 
                                    interference.</p>
                            </li>
                            <li>
                                <h5>Digital Modulator</h5>

                                    <p>A digital modulator takes digital information (like data from a computer) and converts it into a signal that can be 
                                    transmitted over a communication channel (like a radio wave or a cable).</p>
                                    <ul>
                                        <li><strong>Amplitude:</strong> The height of the signal. (Amplitude Shift Keying - ASK)</li>
                                        <li><strong>Frequency:</strong> How quickly the signal oscillates. (Frequency Shift Keying - FSK)</li>
                                        <li><strong>Phase:</strong> The starting point of the signal's oscillation. (Phase Shift Keying - PSK)</li>
                                    </ul>
                                    <br></br>
                            </li>
                            <li>
                                <h5>Channel</h5>
                                <p>The channel is the medium through which the signal travels. It could be physical, like a coaxial cable, or wireless, like radio waves. The channel 
                                    often introduces noise, distortion, or interference, which can degrade the quality of the signal.</p>
                            </li>
                            <li>
                                <h5>Digital Demodulator</h5>
                                <p>At the receiving end, the digital demodulator extracts the transmitted signal from the carrier wave. This step reverses the modulation process, 
                                    recovering the encoded signal for further processing.</p>
                            </li>
                            <li>
                                <h5>Channel Decoder</h5>
                                <p>The channel decoder detects and corrects errors introduced during transmission. Using the redundancy added by the channel encoder, it ensures that 
                                    the signal is as accurate as possible before passing it on to the next stage.</p>
                            </li>
                            <li>
                                <h5>Source Decoder</h5>
                                <p>The source decoder reconstructs the original message from the encoded signal. It decompresses the data and restores it to its original form, 
                                    reversing the processes the source encoder applies.</p>
                            </li>
                            <li>
                                <h5>Output Transducer</h5>
                                <p>Finally, the output transducer converts the electrical signal back into a form that can be understood by the end-user. For instance, in an audio system, 
                                    a speaker converts electrical signals back into sound waves.</p>
                            </li>
                        </ol>

                        <h4>Importance of These Elements</h4>
                        <p>Each component of a digital communication system plays a crucial role in ensuring that the transmitted message is delivered accurately, efficiently, 
                            and securely. By understanding these elements, we can appreciate the sophisticated engineering that underpins modern communication systems, from 
                            streaming a video on your smartphone to sending data across continents.</p>
                        <p>As technology evolves, these foundational elements continue to be refined, paving the way for faster, more reliable, and more efficient communication 
                            systems that drive innovation and connectivity around the globe.</p>
                    </div>
                </div>
            </div>
            </>
        ), 
    },
    3: {
        title: 'Animation of a simple pendulum in Python',
        date: 'Jannuary 8, 20224',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <h1 className="text-center">Animation of a simple pendulum in Python</h1>
                <h5 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h5>
                <p className="text-muted text-center">January 8, 2025</p>
            <pre className="bg-light p-3 rounded">
                <code>
{`
import matplotlib.pyplot as plt
import matplotlib.animation as animation
import numpy as np
import math

# Constants
g = 9.8  # Acceleration due to gravity (m/s^2)
l = 1.0  # Length of the pendulum (m)
theta0 = 80 * np.pi / 180  # Initial angle (radians)
dt = 0.05  # Time step (s)

# Set up the figure and axes
fig, ax = plt.subplots()
line, = ax.plot([], [], 'o-', lw=2)
time_text = ax.text(0.02, 0.95, '', transform=ax.transAxes)  # For displaying time


# Initialize the plot
def init():
    ax.set_xlim(-1.5, 1.5)
    ax.set_ylim(-1.5, 0.5)
    ax.set_aspect('equal')
    ax.grid()
    return line, time_text


# Animation function
theta = theta0
omega = 0.0  # Angular velocity
time = 0.0


def animate(i):
    global theta, omega, time
    alpha = -(g / l) * math.sin(theta)  # Angular acceleration
    omega += alpha * dt  # Update angular velocity
    theta += omega * dt  # Update angular position
    time += dt

    x = l * np.sin(theta)
    y = -l * np.cos(theta)
    line.set_data([0, x], [0, y])
    time_text.set_text(f"Time = {time:.2f} s")
    return line, time_text


# Create the animation
ani = animation.FuncAnimation(fig, animate, init_func=init, frames=200, interval=20, blit=True, repeat=True)

plt.show()

# To save the animation as a GIF (requires imageio or Pillow)
ani.save('pendulum.gif', writer='pillow', fps=30)   # or writer='imagemagick' if you have it installed.
`}
                </code>
            </pre>
            </div></div></div>
            
            </>
        ), 
        imageUrl: '/images/blog/pendulum.gif',
    },
    4: {
        title: 'eUICC and Multi-IMSI',
        date: 'January 9, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
            <h2 className="text-center">Managing mobile network connectivity with eUICC and Multi-IMSI</h2><br></br>
            <h5 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h5>
            </div>
            </div>
            <div className="container mt-5">
                <div className="row justify-content-center">
                <img src="/images/blog/eUICC.svg" alt="Communication Signal Flow" className="responsive-image-l"></img><br></br>
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <h4>eUICC vs. Multi-IMSI</h4>
                        <p>eUICC and Multi-IMSI are technologies that offer flexibility in managing mobile network connectivity, especially for IoT devices 
                            deployed globally. While they share the goal of enabling devices to connect to different networks, they achieve it in distinct ways.</p>

                        <h5>eUICC (embedded Universal Integrated Circuit Card)</h5>
                        <ul>
                            <li><strong>Embedded and Programmable:</strong> eUICC is a SIM card that is embedded directly into a device during manufacturing. It's not removable 
                            like traditional SIMs. The key feature of eUICC is its ability to be remotely provisioned and managed.</li>
                            <li><strong>Remote SIM Provisioning (RSP):</strong> eUICC enables the device to hold multiple operator profiles simultaneously, although only one can be active at a time. 
                            This facilitates seamless switching between operators over-the-air (OTA).
                            It also allows to download and install different operator profiles 
                            (containing network credentials) onto the eUICC over-the-air (OTA). This means you can switch between mobile network operators 
                            (MNOs) without physically changing the SIM card.
                            </li>
                            <li><strong>GSMA Standard:</strong> eUICC is a standard defined by the GSMA (the global association for mobile operators), ensuring interoperability 
                            between different manufacturers and operators. This is crucial for scaling IoT deployments globally.</li>
                            <li><strong>Flexibility and Future-Proofing:</strong> eUICC offers great flexibility for managing connectivity throughout a 
                            device's lifecycle. It's 
                            particularly useful for IoT deployments where devices are in remote locations or where connectivity needs may change over time.</li>
                        </ul>

                        <h5>Multi-IMSI</h5>
                        <ul>
                            <li><strong>Multiple Identities on One SIM:</strong> A Multi-IMSI SIM card contains multiple IMSIs (International Mobile Subscriber 
                            Identities), 
                            each associated with a different mobile network operator. Each IMSI enables the SIM to appear as a subscriber to a different MNO, 
                            effectively allowing the device to access various networks.
                            </li>
                            <li><strong>Switching Between IMSIs:</strong> The SIM card can switch between these IMSIs, allowing the device to connect to 
                            different networks. 
                            This switching can be done automatically based on factors like signal strength or roaming costs, or it can be done manually.
                            The automatic switching feature is based on predefined logic, such as signal strength, quality of service, or cost optimization. This logic is typically 
                            embedded in the SIM firmware or controlled by the service provider.
                            </li>
                            <li><strong>Traditional SIM Format:</strong> While Multi-IMSI is traditionally associated with physical SIMs, it can also be implemented on eUICC as a profile with multiple 
                            IMSIs, offering a hybrid approach.</li>
                            <li><strong>Coverage Focus:</strong> Multi-IMSI is often used to ensure broader coverage by providing access to multiple networks in different regions.
                            Multi-IMSI's primary advantage is its ability to optimize network coverage and roaming costs across regions without 
                            requiring a remote provisioning infrastructure.
                            </li>
                        </ul>

                        <h5 class="key-differences">Key Differences</h5>
                        <ul>
                            <li><strong>Physical vs. Programmable:</strong> Multi-IMSI uses a physical SIM card with pre-loaded IMSIs, while eUICC is 
                            embedded and programmable via RSP.
                            eUICC is a more versatile and forward-looking solution, while Multi-IMSI is simpler to deploy without needing remote management 
                            infrastructure.
                            </li>
                            <li><strong>Remote Management:</strong> eUICC allows for remote provisioning and management of profiles, while Multi-IMSI typically requires some form of local or 
                            pre-configured switching logic.
                            eUICC excels in remote management, making it the preferred choice for long-term deployments and where connectivity requirements may evolve.
                            </li>
                            <li><strong>Flexibility:</strong> eUICC offers greater flexibility in terms of switching operators and managing connectivity over time. 
                            Multi-IMSI provides more immediate access to multiple networks.
                            Multi-IMSI offers quick and reliable access to multiple networks but lacks the dynamic adaptability of eUICC.
                            </li>
                        </ul>

                        <p class="summary"><strong>In Summary</strong><br></br>
                        Both eUICC and Multi-IMSI offer valuable solutions for managing mobile connectivity, especially in IoT. eUICC provides greater 
                        flexibility and future-proofing 
                        through remote management, while Multi-IMSI offers a more straightforward approach to accessing multiple networks with a single SIM. 
                        The best choice depends 
                        on the specific needs and requirements of the deployment.
                        </p>
                        <h5>What is an APN?</h5>
                        <p>APN stands for Access Point Name. It's like a gateway that your device uses to connect to the mobile network's data services.</p>
                        The APN contains specific settings that the network needs to identify and authorize your device for data access. These settings 
                            typically include:
                        <ul>
                            <li><strong>APN name:</strong> A text identifier (e.g., "internet", "data", "web").</li>
                            <li><strong>Username and password:</strong> For authentication (sometimes not required).</li>
                            <li><strong>Authentication type:</strong> How the device authenticates with the network (e.g., PAP, CHAP).</li>
                        </ul>
                        <h5>How APN Works with eUICC and LTE Modem</h5>
                        Here's how APN works with eUICC and LTE modem:
                        <ol>
                            <li><strong>Profile Activation:</strong> When a profile is activated on the eUICC, it provides the IMSI 
                            (International Mobile Subscriber Identity), authentication keys, and other network credentials. The profile may 
                            also include the Access Point Name (APN) settings required for that specific mobile network operator (MNO).</li>
                            <li><strong>LTE Modem Configuration:</strong> The LTE modem retrieves the APN settings from the active profile on the eUICC. 
                            These settings inform the modem how to establish a connection to the MNO's packet-switched network.</li>
                            <li><strong>Data Connection Request:</strong> When the device wants to establish a data connection (e.g., to browse the internet or 
                            use an app), the LTE modem uses the APN settings to send a connection request to the mobile network.</li>
                            <li><strong>Network Authentication:</strong> The mobile network uses the IMSI and authentication keys from the active profile, 
                            along with the APN information, to authenticate the device. The APN also helps the network determine the appropriate gateway and 
                            policies for the connection (e.g., billing, quality of service).</li>
                            <li><strong>Data Connection Established:</strong> Upon successful authentication and validation, the mobile network assigns an 
                            IP address to the device and establishes the data connection, enabling communication over the internet or private network.</li>
                        </ol>

                        <h5>Why APN is important in the context of eUICC:</h5>
                        <ul>
                            <li><strong>Network Compatibility:</strong> Different mobile operators use different APN settings. By storing APN settings within 
                            each profile on the eUICC, the device can automatically configure itself for the correct network when switching profiles.</li>
                            <li><strong>Seamless Switching:</strong> This ensures a smooth and seamless transition between networks without requiring the user 
                            to manually configure APN settings.</li>
                            <li><strong>IoT Deployments:</strong> In IoT applications where devices are deployed remotely, the ability to automatically configure 
                            APN settings is crucial for maintaining connectivity across different networks.</li>
                        </ul>

                        <h5>Key Notes on eUICC and APN</h5>

                        <ul>
                            <li>
                                <strong>eUICC (Embedded Universal Integrated Circuit Card):</strong> is critical for enabling profile switching and 
                                provisioning over-the-air (OTA), making it highly versatile for IoT applications.
                            </li>
                            <li>
                                <strong>The APN (Access Point Name):</strong> acts as a gateway to differentiate between various data services or 
                                private networks on the operator's infrastructure.
                            </li>
                            <li>
                                This workflow is typical for IoT devices and modern LTE deployments, where seamless connectivity and remote management are crucial.
                            </li>
                        </ul>

                        <p><strong>In simple terms:</strong><br></br>
                        Think of the APN as the "address" that your device needs to provide to the mobile network to access the internet. The eUICC stores the 
                        correct "address" for each network in its profiles, and the LTE modem uses that "address" to establish the connection.</p>

                        <p>Without the correct APN settings, the LTE modem wouldn't be able to establish a data connection, even if it has the correct IMSI and 
                            other credentials. Therefore, APN plays a vital role in ensuring that eUICC and LTE modem can work together seamlessly to provide 
                            reliable mobile connectivity.
                        </p>
                        <pre className="bg-light p-3 rounded">
                <code>
{`
[Profile Activation]
         |
         v
[LTE Modem Configuration]
         |
         v
[Data Connection Request]
         |
         v
[Network Authentication]
         |
         v
[Data Connection Established]
`}
</code>
</pre>
                        </div>
                    </div>
                </div>
            </>
        ), 
    },
    5: {
        title: 'Python script to plot the radiation pattern of a half-wave dipole antenna. ',
        date: 'January 10, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
                <h2 className="text-center">Python script to plot the radiation pattern of a half-wave dipole antenna.</h2><br></br>
                <h5 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h5>
            </div>
            <div className="d-flex justify-content-center">
            <img src="/images/blog/dipole.gif" alt="Communication Signal Flow" className="responsive-image-l"></img>
            </div>
            </div>
            <div className="container mt-5">
                <div className="row justify-content-center">
                
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <h4>Python script to plot the radiation pattern of a half-wave dipole antenna. </h4>
                        <pre className="bg-light p-3 rounded">
                <code>
{`
import numpy as np
import matplotlib.pyplot as plt
from matplotlib.animation import FuncAnimation

# Set up the figure
fig = plt.figure(figsize=(8, 8))
ax = plt.subplot(111, polar=True)
line, = ax.plot([], [], color="blue", label="Radiation Pattern")
fill = ax.fill([], [], color="blue", alpha=0.3)
ax.set_title("Animated Dipole Radiation Pattern", va='bottom')
ax.set_rticks([0.2, 0.4, 0.6, 0.8, 1.0])  # Radial ticks
ax.grid(True)
ax.legend(loc="upper right")

# Define theta range
theta = np.linspace(0, 2 * np.pi, 360)


# Function to compute the radiation pattern for a given dipole length
def dipole_pattern(theta, length):
    k = 2 * np.pi / 1  # Assuming length is given in wavelengths
    numerator = np.sin(k * length * np.cos(theta))
    denominator = np.cos(theta)
    pattern = np.abs(numerator / denominator)
    pattern[~np.isfinite(pattern)] = 0  # Handle singularities
    return pattern


# Initialize the plot elements
def init():
    line.set_data([], [])
    ax.text(0.5, 1.05, '', transform=ax.transAxes, ha='center', fontsize=12, color='black', weight='bold')
    return line, fill


# Update function for animation
def update(length):
    pattern = dipole_pattern(theta, length)
    pattern /= np.max(pattern)  # Normalize
    line.set_data(theta, pattern)
    fill[0].remove()
    fill[0] = ax.fill(theta, pattern, color="blue", alpha=0.3)[0]
    ax.texts.clear()
    ax.text(0.5, 1.0, f'Antenna Length: {length:.2f} λ', transform=ax.transAxes, ha='center', fontsize=12,
            color='black', weight='bold')
    return line, fill


# Animation parameters
lengths = np.linspace(0.5, 2.0, 100)  # Varying antenna lengths
ani = FuncAnimation(fig, update, frames=lengths, init_func=init, blit=False, interval=50)

# Save the animation as a GIF
ani.save('dipole.gif', writer='pillow', fps=30)

# Show the animation
plt.show()

`}
                </code>
                </pre>
                <h4>Radiation Pattern of a Half-Wave Dipole Antenna</h4>

    <p>The radiation pattern of a half-wave dipole antenna describes how the power radiated by the antenna varies with direction. It's typically expressed 
        in terms of the electric field intensity as a function of the elevation angle (θ).</p>

    <h5>The Equation</h5>
    <p>Here's the equation for the far-field radiation pattern of a half-wave dipole:</p>

    <div class="equation">
        <em>E(θ, L) = E₀ * [sin(k * cos(θ)) / (k * cos(θ))]</em><br></br>
    </div>

    Where:
    <ul>
        <li><strong>E(θ)</strong> is the radiation intensity at an angle θ for a given dipole length L.</li>
        <li><strong>E₀</strong> is a constant that depends on factors like the input power, the distance from the antenna, and the impedance of free space. 
        It essentially scales the overall magnitude of the radiation pattern.</li>
        <li>k = 2π/L is the wavenumber, with L being the electrical length of the dipole in wavelengths.
        </li>
        <li><strong>θ</strong> is the elevation angle, measured from the axis of the dipole (the z-axis). θ = 0° is along the dipole axis, and θ = 90° is 
        perpendicular to the dipole axis (in the horizontal plane).</li>
        <li><strong>π</strong> is the mathematical constant pi (approximately 3.14159).</li>
    </ul>

    <h5>Key Features and Interpretations</h5>
    <ul>
        <li><strong>Nulls:</strong> When θ = 0° or 180° (along the axis of the dipole), cos(θ) is ±1, and cos((π/2)cos(θ)) becomes cos(π/2), which is 0. 
        This means the radiation is zero along the dipole axis. These are called nulls in the radiation pattern.</li>
        <li><strong>Maximum Radiation:</strong> The maximum radiation occurs at θ = 90° (perpendicular to the dipole axis). In this case, cos(θ) is 0, 
        cos((π/2)cos(θ)) becomes cos(0) which is 1, and sin(θ) is also 1.</li>
        <li><strong>Shape:</strong> The radiation pattern has a characteristic "doughnut" or "figure-eight" shape in 3D. In a 2D cut (typically the 
        E-plane, which is the plane containing the dipole axis), it looks like two lobes.</li>
        <li><strong>Normalization:</strong> Often, the radiation pattern is normalized by dividing E(θ) by its maximum value (which occurs at θ = 90°). 
        This results in a normalized radiation pattern where the maximum value is 1, making it easier to compare the relative radiation intensity at different angles. 
        The normalized equation would be:
          <div class="equation">
            <em></em>
          </div>
        </li>
    </ul>

    <h5>Important Considerations</h5>
    <ul>
        <li><strong>Far-field approximation:</strong> This equation is valid for the far-field region of the antenna, which is at a distance far enough 
        from the antenna that the electromagnetic fields can be approximated as plane waves.</li>
        <li><strong>Thin dipole assumption:</strong> The equation assumes that the dipole is thin compared to its length.</li>
        <li><strong>Ideal conditions:</strong> The equation represents an ideal half-wave dipole in free space. In real-world scenarios, factors like the 
        antenna's environment, the feed point, and the thickness of the wire can affect the actual radiation pattern.</li>
    </ul>

    <p>The radiation pattern is often visualized as a polar plot, where the radial distance from the center represents the radiation intensity at a given angle. 
        For a half-wave dipole, the polar plot would show the characteristic figure-eight shape.</p>

    <p>In summary, the equation <em>E(θ) = E₀ * [cos((π/2)cos(θ)) / sin(θ)]</em> describes the far-field radiation pattern of an ideal half-wave dipole antenna. 
    It shows how the radiated power varies with the elevation angle and explains the characteristic shape of the radiation pattern.</p>
                        </div>
                    </div>
                </div>
            </>
        ), 
    },
    6: {
        title: 'Python script to plot the radiation pattern of a half-wave dipole antenna. ',
        date: 'January 9, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
                <h2 className="text-center">Python code with Matplotlib simulates and visualizes the 3D radiation pattern of a dipole antenna.</h2><br></br>
                <h5 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h5>
                <div className="d-flex justify-content-center">
                    <img src="/images/blog/3d_dipole.gif" alt="3d_dipole" className="responsive-image-l"></img>
                </div>
            </div>
            </div>
            <div className="container mt-2">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <h4>The  Python with the help of Matplotlib code simulates and visualizes the 3D radiation pattern of a dipole antenna.</h4>
                        
                        <pre className="bg-light p-3 rounded">
                <code>
{`
import numpy as np
import matplotlib.pyplot as plt
from mpl_toolkits.mplot3d import Axes3D
from matplotlib.animation import FuncAnimation

# Define the grid for theta and phi
theta = np.linspace(0, np.pi, 180)  # Elevation
phi = np.linspace(0, 2 * np.pi, 360)  # Azimuth
theta, phi = np.meshgrid(theta, phi)


# Function to compute the 3D radiation pattern
def dipole_pattern_3d(theta, phi, length):
    k = 2 * np.pi / length  # Wavenumber
    numerator = np.sin(k * np.cos(theta))
    denominator = k * np.cos(theta)
    pattern = np.abs(numerator / denominator)
    pattern[~np.isfinite(pattern)] = 0  # Handle singularities
    return pattern


# Convert spherical to Cartesian coordinates
def to_cartesian(r, theta, phi):
    x = r * np.sin(theta) * np.cos(phi)
    y = r * np.sin(theta) * np.sin(phi)
    z = r * np.cos(theta)
    return x, y, z


# Set up the figure and axis
fig = plt.figure(figsize=(10, 10))
ax = fig.add_subplot(111, projection='3d')


# Initialize the plot
def init():
    ax.clear()
    ax.set_xlim([-1, 1])
    ax.set_ylim([-1, 1])
    ax.set_zlim([-1, 1])
    ax.set_title("3D Animated Dipole Radiation Pattern")
    return fig,


# Update function for animation
def update(length):
    r = dipole_pattern_3d(theta, phi, length)
    x, y, z = to_cartesian(r, theta, phi)
    ax.clear()
    ax.plot_surface(x, y, z, rstride=5, cstride=5, facecolors=plt.cm.viridis(r / np.max(r)), alpha=0.9)
    ax.set_xlim([-1, 1])
    ax.set_ylim([-1, 1])
    ax.set_zlim([-1, 1])
    ax.set_title(f"3D Dipole Radiation Pattern (Length: {length:.2f} λ)")
    return fig,


# Animation parameters
lengths = np.linspace(0.5, 2.0, 50)  # Antenna lengths
ani = FuncAnimation(fig, update, frames=lengths, init_func=init, blit=False, interval=100)

# Save animation (optional)
ani.save("3d_dipole.gif", writer="pillow", fps=20)

# Show the animation
plt.show()

`}
                </code>
                </pre>
                <h4>The code simulates and visualizes the 3D radiation pattern of a dipole antenna.</h4>
                <p>
                The code defines a function to calculate the 3D radiation pattern of a dipole antenna and creates an animation to visualize this 
                pattern for different antenna lengths. It uses spherical coordinates to define the radiation pattern and then converts it to Cartesian 
                coordinates for plotting. The animation shows how the radiation pattern changes as the antenna length increases.
                </p>
                        </div>
                    </div>
                </div>
    
            </>
        ), 
    },
    7: {
        title: 'Simulating PID loop with Python',
        date: 'January 9, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
            <h2 className="text-center">Python script to simulate operation of a PID loop.</h2><br></br>
            <h5 className="text-center"><a href="https://www.jinetinnovations.com/" target="blank">JI NET Innovations</a></h5>
            </div>
            </div>
            <div className="container mt-2">
                <div className="d-flex justify-content-center">
                    <img src="/images/blog/ui_simulation.gif" alt="Communication Signal Flow" className="responsive-image-l"></img>
                </div> 
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <h4>Python script to simulate operation of a PID loop.</h4>
                        
                        <pre className="bg-light p-3 rounded">
                <code>
{`
import tkinter as tk
from tkinter import ttk
from PIL import ImageGrab, Image
from matplotlib.backends.backend_tkagg import FigureCanvasTkAgg
import matplotlib.pyplot as plt
import time


class PID:
    def __init__(self, Kp, Ki, Kd, setpoint=0):
        self.Kp = Kp
        self.Ki = Ki
        self.Kd = Kd
        self.setpoint = setpoint
        self.integral = 0
        self.previous_error = 0

    def update(self, measured_value, dt):
        error = self.setpoint - measured_value
        self.integral += error * dt
        derivative = (error - self.previous_error) / dt
        output = self.Kp * error + self.Ki * self.integral + self.Kd * derivative
        self.previous_error = error
        return output


class PIDui:
    def __init__(self, root, pid):
        self.root = root
        self.pid = pid
        self.measured_value = 0
        self.dt = 0.1
        self.time_data = []
        self.value_data = []
        self.running = False  # Simulation running state
        self.frames = []  # Store frames for GIF

        self.root.title("PID Controller")

        # Create a frame for the controls
        self.control_frame = ttk.Frame(root)
        self.control_frame.pack(side=tk.LEFT, fill=tk.Y, padx=10, pady=10)

        self.kp_slider = 0
        self.kp_value_label = ttk.Label(self.control_frame, text=f"Kp: {self.pid.Kp:.2f}")
        self.ki_slider = 0
        self.ki_value_label = ttk.Label(self.control_frame, text=f"Ki: {self.pid.Ki:.2f}")
        self.kd_slider = 0
        self.kd_value_label = ttk.Label(self.control_frame, text=f"Kd: {self.pid.Kd:.2f}")
        self.setpoint_slider = 0
        self.setpoint_value_label = ttk.Label(self.control_frame, text=f"Setpoint: {self.pid.setpoint:.2f}")

        # Create sliders for PID parameters
        self.create_sliders()

        # Create a Start button
        self.start_button = ttk.Button(self.control_frame, text="Start", command=self.start_simulation)
        self.start_button.pack(pady=10)

        # Create a Reset button
        self.reset_button = ttk.Button(self.control_frame, text="Reset", command=self.reset_simulation)
        self.reset_button.pack(pady=5)

        # Create a Save GIF button
        self.save_gif_button = ttk.Button(self.control_frame, text="Save GIF", command=self.save_gif)
        self.save_gif_button.pack(pady=5)

        # Create a frame for the plot
        self.plot_frame = ttk.Frame(root)
        self.plot_frame.pack(side=tk.RIGHT, fill=tk.BOTH, expand=True)

        # Create the plot
        self.fig, self.ax = plt.subplots(figsize=(6, 4))
        self.ax.set_title("PID Controller Response")
        self.ax.set_xlabel("Time")
        self.ax.set_ylabel("Measured Value")
        self.line, = self.ax.plot([], [], label="Measured Value")
        self.ax.axhline(self.pid.setpoint, color="r", linestyle="--", label="Setpoint")
        self.ax.legend()

        self.canvas = FigureCanvasTkAgg(self.fig, master=self.plot_frame)
        self.canvas.get_tk_widget().pack(fill=tk.BOTH, expand=True)

    def create_sliders(self):
        # Proportional (Kp)
        ttk.Label(self.control_frame, text="Proportional (Kp):").pack(pady=5, anchor="w")
        self.kp_slider = ttk.Scale(self.control_frame, from_=0, to=10, orient="horizontal", command=self.update_kp)
        self.kp_slider.set(self.pid.Kp)
        self.kp_slider.pack(fill="x")
        self.kp_value_label = ttk.Label(self.control_frame, text=f"Kp: {self.pid.Kp:.2f}")
        self.kp_value_label.pack(pady=2)

        # Integral (Ki)
        ttk.Label(self.control_frame, text="Integral (Ki):").pack(pady=5, anchor="w")
        self.ki_slider = ttk.Scale(self.control_frame, from_=0, to=1, orient="horizontal", command=self.update_ki)
        self.ki_slider.set(self.pid.Ki)
        self.ki_slider.pack(fill="x")
        self.ki_value_label = ttk.Label(self.control_frame, text=f"Ki: {self.pid.Ki:.2f}")
        self.ki_value_label.pack(pady=2)

        # Derivative (Kd)
        ttk.Label(self.control_frame, text="Derivative (Kd):").pack(pady=5, anchor="w")
        self.kd_slider = ttk.Scale(self.control_frame, from_=0, to=1, orient="horizontal", command=self.update_kd)
        self.kd_slider.set(self.pid.Kd)
        self.kd_slider.pack(fill="x")
        self.kd_value_label = ttk.Label(self.control_frame, text=f"Kd: {self.pid.Kd:.2f}")
        self.kd_value_label.pack(pady=2)

        # Setpoint
        ttk.Label(self.control_frame, text="Setpoint:").pack(pady=5, anchor="w")
        self.setpoint_slider = ttk.Scale(self.control_frame, from_=0, to=100, orient="horizontal", command=self.update_setpoint)
        self.setpoint_slider.set(self.pid.setpoint)
        self.setpoint_slider.pack(fill="x")
        self.setpoint_value_label = ttk.Label(self.control_frame, text=f"Setpoint: {self.pid.setpoint:.2f}")
        self.setpoint_value_label.pack(pady=2)

    def update_kp(self, value):
        self.pid.Kp = float(value)
        self.kp_value_label.config(text=f"Kp: {self.pid.Kp:.2f}")

    def update_ki(self, value):
        self.pid.Ki = float(value)
        self.ki_value_label.config(text=f"Ki: {self.pid.Ki:.2f}")

    def update_kd(self, value):
        self.pid.Kd = float(value)
        self.kd_value_label.config(text=f"Kd: {self.pid.Kd:.2f}")

    def update_setpoint(self, value):
        self.pid.setpoint = float(value)
        self.setpoint_value_label.config(text=f"Setpoint: {self.pid.setpoint:.2f}")
        self.ax.lines[1].set_ydata([self.pid.setpoint] * 2)  # Update setpoint line

    def start_simulation(self):
        if not self.running:
            self.running = True
            self.run_simulation()

    def reset_simulation(self):
        self.running = False
        self.measured_value = 0
        self.time_data.clear()
        self.value_data.clear()
        self.line.set_data([], [])
        self.ax.relim()
        self.ax.autoscale_view()
        self.canvas.draw()

    def run_simulation(self):
        if self.running:
            # Simulate one step
            control = self.pid.update(self.measured_value, self.dt)
            self.measured_value += control * self.dt

            # Update data
            if len(self.time_data) == 0:
                self.time_data.append(0)
            else:
                self.time_data.append(self.time_data[-1] + self.dt)
            self.value_data.append(self.measured_value)

            # Update plot
            self.line.set_data(self.time_data, self.value_data)
            self.ax.relim()
            self.ax.autoscale_view()
            self.canvas.draw()

            # Schedule the next update
            self.root.after(100, self.run_simulation)

    def capture_frame(self):
        # Capture the entire window as a frame
        x = self.root.winfo_rootx()
        y = self.root.winfo_rooty()
        w = x + self.root.winfo_width()
        h = y + self.root.winfo_height()
        frame = ImageGrab.grab(bbox=(x, y, w, h))
        self.frames.append(frame)

    def save_gif(self):
        if self.frames:
            self.frames[0].save(
                "ui_simulation.gif",
                save_all=True,
                append_images=self.frames[1:],
                duration=100,
                loop=0,
            )
            print("GIF saved as ui_simulation.gif")


if __name__ == "__main__":
    # Initialize PID controller
    pid = PID(Kp=1.0, Ki=0.1, Kd=0.05, setpoint=10)

    # Start the UI
    root = tk.Tk()
    app = PIDui(root, pid)
    root.mainloop()
`}
                </code>
                </pre>
                <h4>This code simulates a PID controller user interface (UI).</h4>
                <p>
                PID (Proportional-Integral-Derivative) controllers are commonly used in control system applications to regulate the output of a 
                system based on a desired setpoint. This code simulates a UI for a PID controller, allowing users to:
                </p>
                <ul>
                    <li>Adjust PID parameters (Kp, Ki, Kd)</li>
                    <li>Set the desired setpoint</li>
                    <li>Visualize the measured value and setpoint over time</li>
                </ul>
                <p>
                The UI also provides buttons to start, reset, and save a simulation as a GIF animation.<br></br>
                Overall, this code simulates an interactive tool for understanding and experimenting with PID controller behavior.
                </p>

                        </div>
                    </div>
                </div>
    
            </>
        ), 
    },

    8: {
        title: 'Additive White Gaussian Noise',
        date: 'January 23, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
            <h2 className="text-center">This Python code simulates the addition of Additive White Gaussian Noise (AWGN).</h2><br></br>
            <p>
            The provided code simulates the addition of Additive White Gaussian Noise (AWGN) to a clean sinusoidal signal and animates the process to visually 
            display the effect of the noise. An AWGN (Additive White Gaussian Noise) channel is a mathematical model used to simulate the impact of noise on a 
            signal as it travels through a communication channel. Here's a breakdown of the key components of AWGN:<br></br>
            <ul>
                <li><strong>Additive:</strong> The noise is added to the original signal without modifying it. This means the noise is simply superimposed on the signal.</li>
                <li><strong>White:</strong> The noise has a constant power spectral density across all frequencies. This means it affects all parts of the signal's frequency spectrum equally.</li>
                <li><strong>Gaussian:</strong> The noise values follow a normal (Gaussian) distribution, characterized by a mean (usually 0) and a variance (related to noise power). The Gaussian distribution is a bell-shaped curve that describes the probability of different noise values occurring.</li>
            </ul>
            The code defines a function awgn_channel that takes a signal and noise power as input and generates random noise using the np.random.normal function. This noise is 
            scaled to the desired noise power level and added to the original signal to create a noisy signal.
            The animation part of the code uses matplotlib.animation.FuncAnimation to create an animation that gradually increases the noise power from zero to a maximum 
            value. As the noise power increases, the sinusoidal signal becomes more and more corrupted by noise.
            The code also includes sliders that allow the user to adjust the frequency of the sinusoidal signal and the noise power. This allows the user to see how 
            different noise levels affect signals of different frequencies.
            Overall, this code provides a useful tool for visualizing the effects of AWGN on signals.
            </p>
            </div>
            </div>
            <div className="container mt-2">
                <div className="d-flex justify-content-center">
                    <img src="/images/blog/noise_channel/awgn_channel_animation.gif" alt = "awgn_channel_animation" className="responsive-image-l"></img>
                </div>
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <h4>Python script to simulate the addition of Additive White Gaussian Noise (AWGN). </h4>
                        
                        <pre className="bg-light p-3 rounded">
                <code>
{`
import numpy as np
import matplotlib.pyplot as plt
from matplotlib.animation import FuncAnimation, PillowWriter
from matplotlib.widgets import Slider, Button


# Define the AWGN channel function
def awgn_channel(signal, noise_power):
    """
        Defining the AWGN Channel Function:
        The awgn_channel function takes a signal and noise power as input.
        It generates random noise using np.random.normal, scaled to the desired noise power.
        It adds this noise to the original signal and returns the noisy signal.
        Args:
            signal: sinusoid
            noise_power:

        Returns:  signal + noise
    """
    noise = np.random.normal(0, np.sqrt(noise_power), len(signal))
    return signal + noise


# Update function for animation
def update(frame):
    """
    Calculates the noise power for each frame of the animation, gradually increasing it from zero to a maximum value
    frame: This variable represents the current frame number in the animation. It starts at 0 and increments up
    to frames - 1
    frames: This is the total number of frames in the animation. It's a constant value set earlier in the code
    (in this example, it's 50)
    max_noise_power: This is the maximum noise power that will be applied to the signal at the end of the animation.
    It's also a constant value (1.0 in this example).

    The expression (frame / frames) calculates a fraction that represents the progress of the animation.
    When frame is 0 (the first frame), the fraction is 0/frames = 0.
    When frame is frames - 1 (the last frame), the fraction is (frames - 1)/frames, which is very close to 1.
    For any frame in between, the fraction is a value between 0 and 1, representing the proportional progress through
    the animation.
    Example:
        Let's say frames is 50 and max_noise_power is 1.0:
        Frame 0: noise_power = (0 / 50) * 1.0 = 0.0
        Frame 25: noise_power = (25 / 50) * 1.0 = 0.5
        Frame 49: noise_power = (49 / 50) * 1.0 = 0.98
        The noise power increases linearly with the frame number.
    """
    if paused[0]:
        return line_noisy, line_clean
    current_frequency = freq_slider.val
    current_noise_power = noise_slider.val
    global clean_signal
    clean_signal = np.sin(2 * np.pi * current_frequency * time)
    noisy_signal = awgn_channel(clean_signal, current_noise_power)
    line_clean.set_data(time, clean_signal)
    line_noisy.set_data(time, noisy_signal)
    return line_noisy, line_clean


def toggle_pause(event):
    paused[0] = not paused[0]


def reset(event):
    freq_slider.reset()
    noise_slider.reset()


if __name__ == "__main__":
    # Generate a clean sinusoidal signal
    num_samples = 1000
    time = np.linspace(0, 1, num_samples)
    initial_frequency = 5  # Hz
    initial_noise_power = 0.1
    clean_signal = np.sin(2 * np.pi * initial_frequency * time)

    # Parameters for animation
    frames = 50
    max_noise_power = 1.0

    # Create the figure
    fig, ax = plt.subplots()
    plt.subplots_adjust(left=0.1, bottom=0.4)
    ax.set_xlim(0, 0.1)
    ax.set_ylim(-2, 2)
    ax.set_title("AWGN Channel Effect on Sinusoidal Signal")
    ax.set_xlabel("Time (s)")
    ax.set_ylabel("Amplitude")

    line_clean, = ax.plot(time, clean_signal, label="Clean Signal", color="blue")
    line_noisy, = ax.plot([], [], label="Noisy Signal", color="orange")
    ax.legend()

    # Create sliders for frequency and noise power
    ax_freq = plt.axes([0.2, 0.25, 0.65, 0.03])
    ax_noise = plt.axes([0.2, 0.2, 0.65, 0.03])
    freq_slider = Slider(ax_freq, 'Frequency (Hz)', 1, 1000, valinit=initial_frequency)
    noise_slider = Slider(ax_noise, 'Noise Power', 0, 1, valinit=initial_noise_power)

    # Add pause/resume button
    ax_pause = plt.axes([0.4, 0.05, 0.2, 0.04])
    pause_button = Button(ax_pause, 'Pause/Resume')
    paused = [False]

    pause_button.on_clicked(toggle_pause)

    # Create the animation
    ani = FuncAnimation(fig, update, frames=frames, blit=False, interval=100)

    # Add a reset button
    ax_reset = plt.axes([0.8, 0.025, 0.1, 0.04])
    reset_button = Button(ax_reset, 'Reset')

    reset_button.on_clicked(reset)

    # Display the animation
    plt.show()

    # Save the animation as a GIF
    output_filename = "awgn_channel_animation.gif"
    ani.save(output_filename, writer=PillowWriter(fps=20))

    print(f"Animation saved as {output_filename}")

`}
                </code>
                </pre>
                <h4>Additive White Gaussian Noise (AWGN).</h4>
                <p>
                In a communication system, additive noise can be introduced by the receiver's electronic components and amplifiers, or it can be 
                picked up during transmission as interference, a common occurrence with radio signals.<br></br>
                Thermal noise, arising primarily from electronic components and amplifiers within the receiver, is statistically modeled as a Gaussian 
                process. This leads to the widely used additive Gaussian noise channel model. This model is prevalent in communication system analysis 
                and design due to its broad applicability to various physical channels and its mathematical simplicity. Channel attenuation, a common 
                phenomenon where signal strength decreases during transmission, can be readily incorporated into this model. When signal attenuation occurs, 
                the received signal is<br></br>
                r(t) = as(t) + n(t)<br></br>
                where "a" represents the attenuation factor.
                </p>
                        </div>
                    </div>
                </div>
    
            </>
        ), 
    },

    9: {
        title: 'Understanding AT+CPSMS Power Saving Mode Setting',
        date: 'January 9, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
                <h2 className="text-center">Understanding AT+CPSMS Power Saving Mode Setting</h2><br></br>
            </div>
            </div>
            <div className="container mt-5">
            <div className="d-flex justify-content-center">
                <img src="/images/blog/low_power_mode.jpeg" alt="Communication Signal Flow" className="responsive-image" />
            </div>
                <div className="row justify-content-center">
                    <div className="col-md-12">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <p>
                        In the world of cellular IoT, power efficiency is paramount. Devices often need to operate for extended periods on limited battery power. 
                        To achieve this, cellular modules employ various power-saving techniques, one of which is Power Saving Mode (PSM). PSM allows devices to 
                        enter a deep sleep state, minimizing power consumption while remaining registered with the network. This post delves into the AT+CPSMS command, 
                        a crucial tool for configuring PSM on cellular modules.
                        </p>
                        <h4>What is AT+CPSMS?</h4>
                        <p>
                        AT+CPSMS is an AT command used to set or query the PSM parameters of a cellular IoT module. It enables developers to control the device's 
                        power-saving behavior, optimizing it for specific applications and use cases.
                        </p>
                        <h4>How Does PSM Work?</h4>
                        <p>
                        PSM allows the device to detach from the network while maintaining its registration. This means the device can enter a low-power state without 
                        needing to re-establish a connection when it needs to transmit data. When a device enters PSM, it negotiates a sleep cycle with the network. 
                        During this cycle, the device is essentially "offline" and consumes minimal power. Once the sleep cycle is over, the device wakes up, sends a 
                        Tracking Area Update (TAU) to the network, and can then transmit or receive data.
                        </p>
                        <h4>The AT+CPSMS Command</h4>
                        <p>
                        The AT+CPSMS command has the following syntax:
                        </p>
                        
                        <pre className="bg-light p-3 rounded">
                <code>
{`
AT+CPSMS=<mode>,<Requested_Periodic_RAU>,<Requested_GPRS_READY_timer>,<Requested_Periodic_TAU>,<Requested_Active_Time>
AT+CPSMS=<mode>[,<RAU>],[,<GPRS TAU>],[,<Active Time>]
`}
                        </code>
                        </pre>
                        <br></br>
                        <h4>AT+CPSMS Parameters</h4>
                        <p>The AT+CPSMS command configures Power Saving Mode (PSM) on cellular modules.</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>Parameter</th>
                                    <th>Description</th>
                                    <th>Values/Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>&lt;mode&gt;</code></td>
                                    <td>Enables or disables PSM.</td>
                                    <td>
                                        <ul>
                                            <li><code>0</code>: Disable PSM.</li>
                                            <li><code>1</code>: Enable PSM.</li>
                                            <li><code>2</code>: Disable PSM and discard all PSM parameters.</li>
                                        </ul>
                                    </td>
                                </tr>
                                <tr>
                                    <td><code>&lt;Requested_Periodic_RAU&gt;</code></td>
                                    <td>Periodic Routing Area Updates (RAU) in GPRS/EDGE networks.</td>
                                    <td>Often not used in modern LTE networks. Can be set to a dummy value (e.g., "00000000").</td>
                                </tr>
                                <tr>
                                    <td><code>&lt;Requested_GPRS_READY_timer&gt;</code></td>
                                    <td>Related to GPRS/EDGE networks.</td>
                                    <td>Can be set to a dummy value.</td>
                                </tr>
                                <tr>
                                    <td><code>&lt;Requested_Periodic_TAU&gt;</code></td>
                                    <td>Time interval between Tracking Area Updates (TAU).</td>
                                    <td>Determines how often the device wakes up to update its location. Critical for balancing power consumption and network reachability.</td>
                                </tr>
                                <tr>
                                    <td><code>&lt;Requested_Active_Time&gt;</code></td>
                                    <td>Duration the device remains active after waking up from PSM.</td>
                                    <td>Ensures the device has enough time to transmit data before going back to sleep.</td>
                                </tr>
                            </tbody>
                        </table>
                        <h4>Example Usage</h4>
                        <p>
                        To enable PSM with a periodic TAU of 40 minutes and an active time of 30 seconds, you would use the following command
                        </p>
                        <pre className="bg-light p-3 rounded">
                            <code>
{`
AT+CPSMS=1,,,"00000100","00001111"  // Set the requested T3412 value to 40 minutes, and set the requested T3324 value to 30 seconds.
`}
                            </code>
                        </pre>
                        </div>
                        <p>When the T3412 (extended TAU timer) expires, the module will be automatically woken up from PSM.</p>
                        <h5>The Key: It's a Code, Not Just a Number</h5>
                        <p>
                        Those binary strings "00000100" (for Active Time) and "00001111" (for Periodic Time) aren't directly representing 40 minutes and 30 seconds. 
                        Instead, they are encoded values that need to be interpreted based on a specific format defined in the 3GPP specifications 
                        (the standards that govern cellular technologies).
                        </p>
                        <h5>Decoding AT+CPSMS=1,,,"00000100","00001111"</h5>
                        <p>Multiplier and Value: Each binary string is divided into two parts:
                        <ul>
                            <li>The first 3 bits act as a <strong>multiplier</strong>.</li>
                            <li>The last 5 bits represent a <strong>value</strong>.</li>
                        </ul>
                        <ul>
                            <li>1 → PSM is enabled</li>
                            <li>"" (empty RAU) → Not used in LTE-M/NB-IoT</li>
                            <li>"00000100" → TAU (Tracking Area Update)</li>
                            <li>"00001111" → Active Time</li>
                        </ul></p>
                        <p>Looking Up the Multiplier: The 3-bit multiplier is used to look up a corresponding time unit in a table. 
                            This table is defined in the 3GPP specifications. </p>
                        <h5>Interpreting the TAU ("00000100")</h5>
                        <p>This is a binary-coded timer following 3GPP TS 24.008, section 10.5.7.4:
                        <ul>
                            <li>00000100 (binary) = 0x04 (hex)</li>
                            <li>The first three bits (000) indicate the unit: 000 = 10 minutes</li>
                            <li>The last five bits (00100 = decimal 4) indicate the multiplier: 4 x 10 minutes = 40 minutes</li>
                        </ul></p>
                        <p>So, the TAU interval (when the device checks in with the network) is 40 minutes.</p>
                        <h5>Interpreting the Active Time ("00001111")</h5>
                        <p>This is the time the modem is ON:
                        <ul>
                            <li>00001111 (binary) = 0x0F (hex)</li>
                            <li>The first three bits (000) indicate the unit: 000 = 2 seconds</li>
                            <li>The last five bits (01111 = decimal 15) indicate the multiplier: 15 x 2 seconds = 30 seconds</li>
                        </ul> So, the Active Time (how long the device stays reachable after sending data) is 30 seconds.</p>

                        <h4>Final Interpretation</h4>
                        <p>
                        <ul>
                            <li>Power Saving Mode enabled</li>
                            <li>Device will wake up every 40 minutes to send a tracking update</li>
                            <li>After sending data, it remains reachable for 30 seconds before entering deep sleep</li>
                        </ul></p>

                        <h4>Requested_Periodic-RAU</h4>
                        <p>
                        String type. One byte in an 8-bit format. Requested extended 
                        periodic RAU value (T3312) to be allocated to the UE in 
                        GERAN. (e.g. "01000111" equals to 70 hours.) 
                        Bits 5 to 1 represent the binary coded timer value.
                        Bits 6 to 8 define the timer value unit as follows:
                        </p>

                        <p><table>
  <thead>
    <tr>
      <th>Bit 8</th>
      <th>Bit 7</th>
      <th>Bit 6</th>
      <th>Time Unit</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>0</td>
      <td>10 minutes</td>
    </tr>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>1</td>
      <td>1 hour</td>
    </tr>
    <tr>
      <td>0</td>
      <td>1</td>
      <td>0</td>
      <td>10 hours</td>
    </tr>
    <tr>
      <td>0</td>
      <td>1</td>
      <td>1</td>
      <td>2 seconds</td>
    </tr>
    <tr>
      <td>1</td>
      <td>0</td>
      <td>0</td>
      <td>30 seconds</td>
    </tr>
    <tr>
      <td>1</td>
      <td>0</td>
      <td>1</td>
      <td>1 minute</td>
    </tr>
  </tbody>
</table></p>

                        <h4>Requested_GPRS-READY-timer</h4>
                        <p>String type. One byte in an 8-bit format. Requested GPRS READY timer value (T3314) to be allocated to the UE in GERAN. 
                            (e.g. "01001010" equals to 1 hours.) Bits 5 to 1 represent the binary coded timer value. Bits 6 to 8 define the timer value unit as follows:</p>
   <p>                         <table>
  <thead>
    <tr>
      <th>Bit 8</th>
      <th>Bit 7</th>
      <th>Bit 6</th>
      <th>Description</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>0</td>
      <td>Value is incremented in multiples of 2 seconds</td>
    </tr>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>1</td>
      <td>Value is incremented in multiples of 1 minute</td>
    </tr>
    <tr>
      <td>0</td>
      <td>1</td>
      <td>0</td>
      <td>Value is incremented in multiples of decihours</td>
    </tr>
    <tr>
      <td>1</td>
      <td>1</td>
      <td>1</td>
      <td>Value indicates that the timer is deactivated</td>
    </tr>
  </tbody>
</table>
</p>
                        <h4>Requested_Periodic-TAU</h4>
                        <p>String type. One byte in an 8-bit format. Requested extended periodic TAU value (T3412) to be allocated to the UE in 
                            E-UTRAN. (e.g. "00001010" equals to 100 minutes) In the Write Command, when mode is 1 and requested_periodicTAU 
                            is omitted, the value configured last time is used. Bits 5 to 1 represent the binary coded timer value. 
                            Bits 6 to 8 define the timer value unit as follows:
                        </p>
                        <p>
                        <table>
  <thead>
    <tr>
      <th>Bit 8</th>
      <th>Bit 7</th>
      <th>Bit 6</th>
      <th>Description</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>0</td>
      <td>Value is incremented in multiples of 10 minutes</td>
    </tr>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>1</td>
      <td>Value is incremented in multiples of 1 hour</td>
    </tr>
    <tr>
      <td>0</td>
      <td>1</td>
      <td>0</td>
      <td>Value is incremented in multiples of 10 hours</td>
    </tr>
  </tbody>
</table></p>
                        <h4>Requested_Active-Time</h4>
                        <p>String type. One byte in an 8-bit format. Requested Active Time value (T3324) to be allocated to the UE. (e.g. "00001111" equals to 1 minute)
                            Bits 5 to 1 represent the binary coded timer value. Bits 6 to 8 define the timer value unit as follows:
                        </p>
                        
                        <p>
                        <table>
  <thead>
    <tr>
      <th>Bit 8</th>
      <th>Bit 7</th>
      <th>Bit 6</th>
      <th>Description</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>0</td>
      <td>Value is incremented in multiples of 2 seconds</td>
    </tr>
    <tr>
      <td>0</td>
      <td>0</td>
      <td>1</td>
      <td>Value is incremented in multiples of 1 minute</td>
    </tr>
    <tr>
      <td>0</td>
      <td>1</td>
      <td>0</td>
      <td>Value is incremented in multiples of decihours</td>
    </tr>
    <tr>
      <td>1</td>
      <td>1</td>
      <td>1</td>
      <td>Value indicates that the timer is deactivated</td>
    </tr>
  </tbody>
</table>
                        </p>
                        <h4>Considerations</h4>
                        <p>
                        <ul>
                            <li><b>Network Support:</b> PSM must be supported by both the cellular module and the network operator.</li>
                            <li><b>Parameter Values:</b> The specific values for TAU and active time depend on the application's requirements and should be 
                            chosen carefully to optimize power consumption and network connectivity.</li>
                            <li><b>Testing:</b> It's crucial to thoroughly test PSM configurations to ensure the device behaves as expected and maintains network 
                            connectivity.</li>
                        </ul></p>

                        <h4>Disabling PSM:</h4>
                        <pre className="bg-light p-3 rounded">
                            <code>
{`
AT+CPSMS=0
`}
                            </code>
                        </pre>

                        <h2>Conclusion</h2>
                        <p>The AT+CPSMS command is a powerful tool for managing power consumption in cellular IoT devices. By understanding its parameters and 
                            using it effectively, developers can significantly extend the battery life of their devices, enabling long-lasting and efficient IoT 
                            solutions.
                        </p>
                    </div>
                </div>
    
            </>
        ), 
    },

    10: {
        title: 'The Hidden Complexity of 4G LTE',
        date: 'January 9, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
            <h2 className="text-center">The Hidden Complexity of 4G LTE: A Symphony of Protocols, Radio Waves, and Error-Fighting Magic.</h2><br></br>
            </div>
            </div>
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center">
                        <img src="/images/blog/symphony1.jpeg" alt="Communication Signal Flow" width="100%" height="100%" display="flex" justify-content="center"></img><br></br>
                        </div>
                        <p>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Everyone's talking about AI, which is understandable. But I think we sometimes forget just how complex 4G LTE is. When you dive into it, 
                        the engineering is incredibly sophisticated. It's easy to take for granted, but it's a huge technological feat.<br></br>
                        Imagine streaming crystal-clear 4K video on your phone while zooming down the highway or joining a seamless video 
                        call in a packed stadium. These experiences feel effortless, but behind the scenes, 4G LTE operates with the precision of a symphony orchestra. 
                        From complex protocol stacks to advanced radio engineering and nonstop error correction, this technology is a masterpiece of human innovation. 
                        Let's dive in and explore why this technology is anything but simple.
                        </p>
                        <h4>The Backbone of Our Connected World</h4>
                        <p>
                        4G LTE (Long-Term Evolution) isn't just "fast mobile internet"—it's a carefully engineered system that delivers speeds up to 100 Mbps 
                        (and beyond) while juggling millions of users. Introduced in 2009, it transformed how we live, work, and play. But it's magic isn't in 
                        the headlines—it's in the details: a stack of protocols, a dance of radio signals, and a relentless fight against data chaos. Buckle up 
                        as we dive into the complexity.
                        </p>
                        <h4>Protocol Stacks: The Multitasking Maestro</h4>
                        <p>
                        At the core of 4G LTE lies its protocol stack—a meticulously structured, multi-layered framework where every layer plays a crucial role, 
                        and none can afford to falter. Think of it as a relay race, where data is handed off seamlessly between layers, each performing its specialized 
                        task to ensure speed, reliability, and efficiency:
                        </p>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid black' }}>
                            <th style={{ borderRight: '1px solid black', padding: '8px' }}>Layer</th>
                            <th style={{ padding: '8px' }}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid gray' }}>
                            <td style={{ borderRight: '1px solid black', padding: '8px' }}>Physical Layer (PHY)</td>
                            <td style={{ padding: '8px' }}>
                                This is where bits meet the air. It handles modulation, turning 1s and 0s into radio waves using techniques
                                like Orthogonal Frequency-Division Multiplexing (OFDM). It's a noisy world out there, and PHY has to scream
                                over the interference.
                            </td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid gray' }}>
                            <td style={{ borderRight: '1px solid black', padding: '8px' }}>Medium Access Control (MAC)</td>
                            <td style={{ padding: '8px' }}>
                                Think of MAC as the traffic cop, deciding who gets to talk and when. It schedules data to avoid collisions
                                in a crowded network.
                            </td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid gray' }}>
                            <td style={{ borderRight: '1px solid black', padding: '8px' }}>Radio Link Control (RLC)</td>
                            <td style={{ padding: '8px' }}>
                                RLC chops data into manageable packets, reassembles them on the other end, and flags anything lost in
                                transit. It's meticulous—and it has to be.
                            </td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid gray' }}>
                            <td style={{ borderRight: '1px solid black', padding: '8px' }}>Packet Data Convergence Protocol (PDCP)</td>
                            <td style={{ padding: '8px' }}>
                                This layer compresses headers and encrypts data, ensuring efficiency and security as packets zip across the
                                network.
                            </td>
                            </tr>
                            <tr style={{ borderBottom: '2px solid black' }}>
                            <td style={{ borderRight: '1px solid black', padding: '8px' }}>Radio Resource Control (RRC)</td>
                            <td style={{ padding: '8px' }}>
                                The brains of the operation, RRC manages connections, handovers (like when you move between cell towers),
                                and resource allocation. It's the conductor keeping the symphony in tune.
                            </td>
                            </tr>
                        </tbody>
                        </table>

                        <p>
                        Each layer depends on the others, and a hiccup in one can derail the whole performance. Coordinating them in real-time, across millions of devices, 
                        is a logistical nightmare—and yet, LTE pulls it off.
                        </p>
                        <h4>Radio Communication: Taming the Airwaves</h4>
                        <p>
                        Now, let's talk about getting data from your phone to a tower—or vice versa. The “air interface” is where 4G LTE flexes its radio muscles, 
                        and it's a battlefield of physics. LTE uses OFDM to split data across multiple subcarriers, like sending a message through dozens of tiny 
                        megaphones instead of one big one. This fights interference and fading—those moments when your signal drops in a tunnel or elevator.
                        Then there's Multiple Input Multiple Output (MIMO), which uses multiple antennas at both ends to send and receive data simultaneously. 
                        It's like juggling several conversations at once, boosting speed and reliability. Add in spectrum management—squeezing efficiency from 
                        limited frequency bands—and you've got a system that's constantly adapting to noise, distance, and demand. It's a high-wire act, and 
                        LTE rarely stumbles.
                        </p>
                        <h4>Error Correction: Fixing Chaos on the Fly</h4>
                        <p>
                        Even with all this sophistication, data doesn't always arrive intact. Radio signals get garbled by buildings, weather, or other devices. 
                        Enter error correction—the unsung hero of 4G LTE.
                        </p>
                        <ul>
                            <li>
                            <b>Turbo Codes:</b> These are like master codebreakers, using clever math to reconstruct corrupted data. They're so efficient they approach the 
                            theoretical limit of error correction (a nod to Shannon's Law for the nerds out there).
                            </li>
                            <li>
                            <b>Hybrid Automatic Repeat Request (HARQ):</b> If Turbo Codes can't fix it, HARQ steps in. It's a tag-team effort: the receiver 
                            flags bad packets, and the sender retransmits only what's needed, all in milliseconds. This keeps your video call from turning 
                            into a pixelated mess.
                            </li>
                        </ul>
                        <p>
                        These systems run non-stop, battling entropy to ensure your Netflix binge or critical work email arrives intact. It's a relentless, 
                        real-time war against chaos—and you barely notice it's happening.
                        </p>
                        <h4>The Bigger Picture</h4>
                        <p>
                        Step back, and the complexity snaps into focus. Protocol stacks orchestrate data flow. Radio tech wrestles with the physical world. 
                        Error correction patches the cracks. Together, they form a system so robust yet so intricate that it's easy to take for granted. It's 
                        like a skyscraper: you marvel at the view, not the thousands of bolts holding it up.
                        Next time your phone seamlessly switches towers or buffers a live stream, think of the engineers who tamed this beast. And as 5G builds 
                        on these foundations, the complexity only deepens—another story for another day.
                        </p>
                        <h4>What's Your Take?</h4>
                        <p>
                        Have you ever paused to appreciate the tech powering your pocket? Or maybe you're one of the brilliant minds designing these systems—drop 
                        a comment and share your insights! Either way, 4G LTE reminds us: the smoother something feels, the harder it's working behind the scenes.
                        </p>
                        </div>
                    </div>
                </div>
    
            </>
        ), 
    },
    
    11: {
        title: 'Understanding OFDM Modulation in LTE',
        date: 'September 14, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
            <h2 className="text-center">Understanding OFDM Modulation in LTE: The Backbone of High-Speed Mobile Data</h2><br></br>
            </div>
            </div>
            <div className="container mt-5">
                <div className="row justify-content-center">
                <img src="/images/blog/ofdm.jpg" alt="Communication Signal Flow" width="100%" height="100%" display="flex" justify-content="center"></img><br></br>
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                        <br></br>
                        <h4>Understanding OFDM Modulation in LTE: The Backbone of High-Speed Mobile Data</h4>
                        <b>Orthogonal Frequency Division Multiplexing (OFDM)</b> is a cornerstone of modern wireless communication, 
                        powering high-speed data in Long-Term 
                        Evolution (LTE) networks. If you've ever streamed a video or browsed the internet on your smartphone, 
                        you've likely benefited from OFDM's efficiency. 
                        In this post, we'll dive into what OFDM is, why it's critical for LTE, and how it works to deliver fast, 
                        reliable mobile connectivity.
                        <h5>What is OFDM?</h5> 
                        OFDM is a digital modulation technique that splits a high-speed data stream into multiple lower-speed subcarriers. 
                        These subcarriers are transmitted simultaneously over a range of frequencies, each carrying a portion of the data. The "orthogonal" 
                        part of OFDM refers to the mathematical property that ensures these subcarriers don't interfere with each 
                        other, even when their frequencies overlap slightly. This clever design maximizes spectrum efficiency and 
                        minimizes signal distortion.
                        Think of OFDM like a highway with multiple lanes. Instead of sending all traffic (data) through one 
                        congested lane, OFDM divides the traffic across several lanes (subcarriers), allowing smoother and faster 
                        travel.<br></br>
                        <h5>Why OFDM in LTE?</h5>
                        LTE, the standard for 4G mobile networks, relies on OFDM for its downlink (base station to device) because of its ability to handle the 
                        challenges of wireless communication. Here's why OFDM is a perfect fit for LTE:
                        <ol>
                        <li><b>Robustness Against Multipath Fading: In urban environments, signals bounce off buildings, 
                            creating multiple paths to the receiver.</b></li> 
                        This can cause interference and signal degradation. OFDM's use of multiple subcarriers, each with 
                        a narrow bandwidth, makes it less sensitive to these distortions. It also employs a cyclic prefix—a guard 
                        interval that absorbs delays from multipath effects.
                        <li><b>High Spectral Efficiency:</b></li> LTE needs to deliver high data rates in limited spectrum bands. 
                        OFDM's orthogonal subcarriers allow more data to be packed into the same frequency range, making it 
                        ideal for bandwidth-hungry applications like video streaming and online gaming.
                        <li><b>Scalability:</b></li> OFDM supports flexible bandwidth allocation (from 1.4 MHz to 20 MHz in LTE), 
                        enabling operators to adapt to different spectrum availability and network demands.
                        <li><b>Simplified Equalization:</b></li> In traditional single-carrier systems, equalizing the signal to 
                        correct for channel distortions is complex. OFDM simplifies this by breaking the signal into narrowband 
                        subcarriers, where the channel appears flat, reducing the need for complex equalization.
                        </ol>   

                        <h5>How OFDM Works in LTE</h5>
                        Let's break down the key components of OFDM in LTE:<br></br><br></br>
                        <b>Subcarriers and Spacing:</b> LTE uses subcarriers spaced 15 kHz apart. A typical 20 MHz LTE channel 
                        can carry up to 1200 subcarriers, each modulated with data using schemes like QPSK, 16-QAM, or 64-QAM, 
                        depending on signal quality.<br></br><br></br>
                        <b>Cyclic Prefix:</b> To combat multipath interference, LTE adds a cyclic prefix to each OFDM symbol. 
                        This is a copy of the symbol's end appended to its beginning, acting as a buffer against signal delays. 
                        LTE supports normal (4.7 µs) and extended (16.7 µs) cyclic prefixes for different environments.<br></br>
                        Resource Blocks: LTE organizes subcarriers into resource blocks (RBs), each containing 12 subcarriers 
                        (180 kHz) over a 0.5 ms slot. These blocks are allocated to users dynamically, optimizing network 
                        performance.Fast Fourier Transform (FFT): OFDM relies on FFT, a mathematical algorithm, to convert 
                        time-domain signals into frequency-domain subcarriers and vice versa. This enables efficient modulation 
                        and demodulation at the transmitter and receiver.

                        <h5>OFDM vs. SC-FDMA in LTE</h5>
                        While OFDM is used for LTE's downlink, the uplink (device to base station) employs Single-Carrier 
                        Frequency Division Multiple Access (SC-FDMA). 
                        SC-FDMA is similar to OFDM but has a lower peak-to-average power ratio (PAPR), which reduces power 
                        consumption in user devices—a critical factor for battery life. Despite this difference, both techniques 
                        leverage the benefits of frequency-domain processing and orthogonality.
                        Challenges and Trade-offs
                        While OFDM is powerful, it's not without challenges:<br></br>
                        High PAPR: OFDM signals can have high power variations, requiring more efficient power amplifiers 
                        in base stations to avoid distortion. Sensitivity to Frequency Offset: Subcarrier orthogonality 
                        depends on precise frequency alignment. Any offset (e.g., from Doppler shifts) 
                        can cause inter-carrier interference.<br></br>
                        Overhead from Cyclic Prefix: The cyclic prefix adds redundancy, slightly reducing data throughput.
                        Despite these trade-offs, OFDM's benefits far outweigh its drawbacks, making it indispensable for 
                        LTE and beyond.
                        The Future: OFDM in 5G and Beyond
                        OFDM's success in LTE has carried over to 5G, where it remains a key modulation scheme, enhanced 
                        with features like flexible numerology (variable subcarrier spacing) to support diverse use cases, 
                        from IoT to ultra-low-latency applications. As wireless technology evolves, OFDM's principles continue 
                        to shape the future of high-speed connectivity.
                        Conclusion<br></br>
                        OFDM is the unsung hero behind LTE's ability to deliver fast, reliable mobile data. Its ability to manage 
                        interference, maximize spectrum efficiency, and adapt to varying conditions makes it a linchpin of modern 
                        wireless networks. Whether you’re streaming a movie or video-calling a friend, OFDM is working behind the 
                        scenes to keep you connected. As we move toward 5G and beyond, OFDM’s legacy will continue to drive the 
                        evolution of mobile communication.
                        Got questions about LTE or OFDM? Drop them in the comments, and let's keep the conversation going!
                        <pre className="bg-light p-3 rounded">
                <code>
{`

import numpy as np
import matplotlib.pyplot as plt

# Parameters
num_subcarriers = 64  # Number of subcarriers
cp_length = 16  # Cyclic prefix length
mod_order = 4  # QPSK modulation (4-QAM)
num_symbols = 10  # Number of OFDM symbols
snr_db = 20  # Signal-to-noise ratio in dB

# Generate random data
num_bits = num_subcarriers * num_symbols * int(np.log2(mod_order))
data_bits = np.random.randint(0, 2, num_bits)


# QPSK modulation
def qpsk_modulate(bits):
    constellation = np.array([1 + 1j, 1 - 1j, -1 + 1j, -1 - 1j]) / np.sqrt(2)
    symbols = np.zeros(len(bits) // 2, dtype=complex)
    for i in range(0, len(bits), 2):
        symbol_idx = bits[i] * 2 + bits[i + 1]
        symbols[i // 2] = constellation[symbol_idx]
    return symbols


# Modulate data
data_symbols = qpsk_modulate(data_bits)
data_matrix = data_symbols.reshape(num_symbols, num_subcarriers)

# IFFT to generate time-domain signal
time_signal = np.zeros((num_symbols, num_subcarriers), dtype=complex)
for i in range(num_symbols):
    time_signal[i] = np.fft.ifft(data_matrix[i])

# Add cyclic prefix
ofdm_signal = np.zeros((num_symbols, num_subcarriers + cp_length), dtype=complex)
for i in range(num_symbols):
    ofdm_signal[i] = np.concatenate([time_signal[i, -cp_length:], time_signal[i]])

# Flatten for transmission
transmitted_signal = ofdm_signal.flatten()

# Add AWGN noise
signal_power = np.mean(np.abs(transmitted_signal) ** 2)
sigma = np.sqrt(signal_power / (10 ** (snr_db / 10)))
noise = (np.random.randn(len(transmitted_signal)) + 1j * np.random.randn(len(transmitted_signal))) * sigma / np.sqrt(2)
received_signal = transmitted_signal + noise

# Reshape received signal
received_signal = received_signal.reshape(num_symbols, num_subcarriers + cp_length)

# Remove cyclic prefix (corrected)
received_time_signal = received_signal[:, cp_length:]

# FFT to recover frequency-domain signal
received_data = np.zeros((num_symbols, num_subcarriers), dtype=complex)
for i in range(num_symbols):
    received_data[i] = np.fft.fft(received_time_signal[i])


# Demodulate QPSK
def qpsk_demodulate(symbols):
    constellation = np.array([1 + 1j, 1 - 1j, -1 + 1j, -1 - 1j]) / np.sqrt(2)
    bits = np.zeros(len(symbols) * 2, dtype=int)
    for i, symbol in enumerate(symbols):
        idx = np.argmin(np.abs(symbol - constellation))
        bits[2 * i] = idx // 2
        bits[2 * i + 1] = idx % 2
    return bits


# Demodulate received data
received_bits = qpsk_demodulate(received_data.flatten())

# Calculate BER
ber = np.mean(data_bits != received_bits)
print(f"Bit Error Rate (BER): {ber:.4f}")

# Plotting
plt.figure(figsize=(12, 8))

# Original data symbols
plt.subplot(2, 2, 1)
plt.scatter(data_symbols.real, data_symbols.imag, c='b', label='Transmitted Symbols')
plt.title('QPSK Constellation (Tx)')
plt.xlabel('Real')
plt.ylabel('Imag')
plt.grid(True)
plt.legend()

# Received data symbols
plt.subplot(2, 2, 2)
plt.scatter(received_data.flatten().real, received_data.flatten().imag, c='r', label='Received Symbols')
plt.title('QPSK Constellation (Rx)')
plt.xlabel('Real')
plt.ylabel('Imag')
plt.grid(True)
plt.legend()

# Time-domain signal
plt.subplot(2, 2, 3)
plt.plot(np.abs(transmitted_signal[:num_subcarriers + cp_length]), label='Tx Signal')
plt.title('Time-Domain OFDM Signal')
plt.xlabel('Sample')
plt.ylabel('Amplitude')
plt.grid(True)
plt.legend()

# Frequency-domain signal
plt.subplot(2, 2, 4)
plt.plot(np.abs(np.fft.fft(transmitted_signal[:num_subcarriers])), label='Tx Spectrum')
plt.title('Frequency-Domain OFDM Signal')
plt.xlabel('Subcarrier')
plt.ylabel('Amplitude')
plt.grid(True)
plt.legend()

plt.tight_layout()
plt.show()

`}
                </code>
                </pre>
                        </div>
                    </div>
                </div>
    
            </>
        ), 
    },

    12: {
        title: 'Title',
        date: 'January 9, 2025',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
            <h2 className="text-center"><a href="/blogs/sbc_wifi_connect_disconnect_blog.html">Linux Wi-Fi Management</a></h2><br></br>
            </div>
            </div>
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <a href="/blogs/sbc_wifi_connect_disconnect_blog.html">
                        <img src="/images/blog/wifi_linux_managment.png" alt="Wi-Fi in Linux" width="100%" height="100%" display="flex" justify-content="center"></img><br></br>
                    </a>
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                            <br></br>
                    </div>
                </div>
            </div>
    
            </>
        ), 
    },

    13: {
        title: 'Title',
        date: 'July 29, 2026',
        content: (
            <>
            <div className="container mt-5">
            <div className="row justify-content-center">
                <h2 className="text-center"><a href="/blogs/scientists.html">Great Scientists (click on here to view the list)</a></h2><br></br>
            </div>
            </div>
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <a href="/blogs/scientists.html">
                        <img src="/images/blog/scientists.jpg" alt="Wi-Fi in Linux" width="100%" height="100%" display="flex" justify-content="center"></img><br></br>
                    </a>
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center"></div>
                            <br></br>
                    </div>
                </div>
            </div>
    
            </>
        ), 
    },
    
    
  };


const BlogPost = () => {
  const { id } = useParams();
  const post = blogPosts[id];

  if (!post) {
    return <p>Blog post not found.</p>;
  }

  return (
    <div className="blog-post">
        <Link to="/blog">Back to Blog List</Link>
        <h4>{post.title}</h4>
        <p><em>{post.date}</em></p>
        {post.imageUrl && <img src={post.imageUrl} alt={post.title} className="responsive-image" style={styles.image} />}
        <div>{post.content}</div>
    </div>
  );
};

// Add CSS media queries inside a global CSS file or styled-components:

const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: '40px',
    },
    main: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    post: {
      padding: '20px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#f9f9f9',
    },
    date: {
      fontSize: '0.9em',
      color: '#888',
      marginBottom: '10px',
    },
    image: {
        width: '60%',
        height: 'auto',
        borderRadius: '8px',
        marginBottom: '2%',
        display: 'block', // Add this
        marginLeft: 'auto', // Add this
        marginRight: 'auto' // Add this
    },
    code: {
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      backgroundColor: '#f4f4f4',
      padding: '10px',
      borderRadius: '4px',
      overflowX: 'auto', // Enable horizontal scrolling
      display: 'block',  // Ensure it behaves like a block element
    },
  };

export default BlogPost;
